import type { UpdateEvents } from "../../../../common/types.ts";
import useTitleBar from "../hooks/useTitleBar.tsx";
import router, { type Context, makeRegex } from "../router/index.ts";
import { local, localActions } from "./local.ts";
import { realtimeUpdate } from "./realtimeUpdate.ts";
import { toWorker } from "./toWorker.ts";
import { create } from "zustand";
import { routeInfos } from "./routeInfos.ts";
import { helpers } from "./helpers.ts";
import i18n from "./i18n.ts";
import { translateErrorMessage } from "./errorMessageLocalized.ts";
import { EmptyState } from "../components/EmptyState.tsx";

/**
 * Things that might be nice, to improve this:
 *
 * - remove tight coupling with router
 * - automatically push updateEvents to other tabs, if there are any updateEvents
 * - good way to handle navigation+updateEvents, where navigation is only one tab but updateEvents go to other tabs
 * - if this is a refresh, check if an exact same refresh is in queue already. if so, discard
 * - tests
 */

type Action = {
	url?: string;
	refresh: boolean;
	replace?: boolean;
	updateEvents: UpdateEvents;
	raw?: Record<string, unknown>;
};

type ActionWithResolve = Action & {
	resolve: () => void;
};

export type LoadingError = {
	id: string;
	kind: "generic" | "leagueNotFound";
	timedOut: boolean;
};

type State = {
	Component: any;
	loading: boolean;
	loadingError: LoadingError | undefined;
	idLoaded: string | undefined;
	idLoading: string | undefined;
	inLeague: boolean;
	data: Record<string, any>;
	scrollToTop: boolean;
};

type ViewInfo = {
	Component: any;
	id: string;
	inLeague: boolean;
	context: Context;
};

export const useViewData = create<
	State & {
		actions: {
			clearLoadingError: () => void;
			startLoading: (idLoading: string) => void;
			doneLoading: (idLoaded: string) => void;
			failLoading: (id: string, error: unknown) => void;
			reset: (state: State) => void;
		};
	}
>((set) => ({
	Component: undefined,
	loading: false,
	loadingError: undefined,
	idLoaded: undefined,
	idLoading: undefined,
	inLeague: false,
	data: {},
	actions: {
		clearLoadingError: () => set({ loadingError: undefined }),
		startLoading: (id: string) =>
			set({ idLoading: id, loading: true, loadingError: undefined }),
		doneLoading: (id: string) =>
			set({
				idLoaded: id,
				idLoading: undefined,
				loading: false,
				loadingError: undefined,
			}),
		failLoading: (id: string, error: unknown) =>
			set({
				idLoading: undefined,
				loading: false,
				loadingError: {
					id,
					kind:
						error instanceof Error && error.message === "League not found."
							? "leagueNotFound"
							: "generic",
					timedOut: error instanceof PageLoadTimeoutError,
				},
			}),
		reset: (state: State) => {
			set(state);
		},
	},
	scrollToTop: false,
}));

const actions = useViewData.getState().actions;

const PAGE_LOAD_TIMEOUT_MS = 30_000;

class PageLoadTimeoutError extends Error {
	constructor() {
		super("Page load timed out");
		this.name = "PageLoadTimeoutError";
	}
}

const withPageLoadTimeout = async <T,>(
	promise: Promise<T>,
	startedAt: number,
): Promise<T> => {
	const timeoutMs = PAGE_LOAD_TIMEOUT_MS - (Date.now() - startedAt);
	if (timeoutMs <= 0) {
		throw new PageLoadTimeoutError();
	}

	let timeoutID: ReturnType<typeof setTimeout> | undefined;

	try {
		return await Promise.race([
			promise,
			new Promise<never>((_resolve, reject) => {
				timeoutID = setTimeout(() => {
					reject(new PageLoadTimeoutError());
				}, timeoutMs);
			}),
		]);
	} finally {
		if (timeoutID !== undefined) {
			clearTimeout(timeoutID);
		}
	}
};

const ErrorMessage = ({ errorMessage }: { errorMessage: string }) => {
	const marketAction = (
		<a className="btn btn-primary" href={helpers.leagueUrl(["market"])}>
			{i18n.t("errorMessage.marketAction", {
				ns: "ui",
				defaultValue: "Return to market",
			})}
		</a>
	);

	const leagueDashboardAction = (
		<a className="btn btn-primary" href={helpers.leagueUrl([])}>
			{i18n.t("errorMessage.leagueDashboardAction", {
				ns: "ui",
				defaultValue: "Go to league dashboard",
			})}
		</a>
	);

	const emptyState =
		errorMessage === "Trade not found."
			? {
					title: i18n.t("errorMessage.tradeNotFoundTitle", {
						ns: "ui",
						defaultValue: "Trade summary unavailable",
					}),
					body: i18n.t("errorMessage.tradeNotFoundBody", {
						ns: "ui",
						defaultValue:
							"This trade summary could not be found. Trade summaries are only generated after a completed trade.",
					}),
				}
			: errorMessage === "All-Star teams not found"
				? {
						title: i18n.t("errorMessage.allStarTeamsNotFoundTitle", {
							ns: "ui",
							defaultValue: "All-Star teams not generated yet",
						}),
						body: i18n.t("errorMessage.allStarTeamsNotFoundBody", {
							ns: "ui",
							defaultValue:
								"All-Star teams are not available yet. They will appear when the league reaches the All-Star stage.",
						}),
					}
				: errorMessage === "Expansion draft protection not active."
					? {
							title: i18n.t("errorMessage.expansionDraftProtectionInactiveTitle", {
								ns: "ui",
								defaultValue: "No expansion draft protection list",
							}),
							action: marketAction,
							body: i18n.t("errorMessage.expansionDraftProtectionInactiveBody", {
								ns: "ui",
								defaultValue:
									"There is no expansion draft protection list right now. Protection lists only appear when the league is in the expansion draft protection phase.",
							}),
						}
					: errorMessage === "Expansion draft team selection required."
						? {
								title: i18n.t("errorMessage.expansionDraftTeamSelectionTitle", {
									ns: "ui",
									defaultValue: "Expansion draft team selection required",
								}),
								action: leagueDashboardAction,
								body: i18n.t("errorMessage.expansionDraftTeamSelectionBody", {
									ns: "ui",
									defaultValue:
										"This expansion draft requires choosing an expansion team before protecting players.",
								}),
							}
				: undefined;

	const titleBarTitle =
		errorMessage === "Expansion draft protection not active."
			? i18n.t("protectPlayers.p2s07Title", {
					ns: "ui",
					defaultValue: "Expansion protection list",
				})
			: (emptyState?.title ??
				i18n.t("common.error", { ns: "ui", defaultValue: "Error" }));

	useTitleBar({ title: titleBarTitle });

	if (emptyState) {
		const action = "action" in emptyState ? emptyState.action : undefined;

		return (
			<EmptyState action={action} title={emptyState.title}>
				{emptyState.body}
			</EmptyState>
		);
	}

	return <p>{translateErrorMessage(errorMessage)}</p>;
};

class ViewManager {
	queue: ActionWithResolve[];
	viewData: Record<string, unknown>;
	idLoaded: string | undefined;
	processingAction: boolean;
	routes: {
		id: string;
		regex: RegExp;
	}[];

	// When navigation to a new URL happens (can be from clicking a link in which case it goes directly to fromRouter, or from realtimeUpdate in which case it goes to fromRealtimeUpdate first and then eventually fromRouter) we want to be able to discard any in-progress load. Do that by keeping track of a symbol associated with a navigation.
	lastNavigationSymbol: symbol;

	constructor() {
		this.queue = [];
		this.viewData = {};
		this.processingAction = false;
		this.lastNavigationSymbol = Symbol();

		this.routes = [];
		for (const [path, id] of Object.entries(routeInfos)) {
			const { regex } = makeRegex(path);
			this.routes.push({
				id,
				regex,
			});
		}
	}

	private clearQueue() {
		for (const action of this.queue) {
			action.resolve();
		}
		this.queue = [];
	}

	async fromRouter(viewInfo: ViewInfo) {
		// If coming from initAction, state will contain navigationSymbol, and it will have already been set to this.lastNavigationSymbol
		if (viewInfo.context.state.navigationSymbol) {
			if (
				this.lastNavigationSymbol !== viewInfo.context.state.navigationSymbol
			) {
				// Must have been another navigation before this one processed
				return;
			}
		} else {
			// If coming only from router (like user clicked a link) then we set lastNavigationSymbol here and clear the queue
			this.lastNavigationSymbol = Symbol();
			this.clearQueue();
		}

		await this.processUpdate(viewInfo, this.lastNavigationSymbol);
	}

	fromRealtimeUpdate(action: Action) {
		// Return a promise because sometimes we want to wait for an update to process before continuing. For example, when simming multiple games, we want to update the UI between each day.
		return new Promise<void>((resolve) => {
			let navigationEvent = false;
			if (action.url) {
				// It's a "navigation event" if it is moving to a new page, rather than just changing some parameter of a page (like abbrev or season). So we need to get the id of this url and compare it to idLoaded.
				let id;
				const urlToMatch = action.url.split("?")[0]!.split("#")[0]!;
				for (const route of this.routes) {
					const m = route.regex.exec(urlToMatch);

					if (m) {
						id = route.id;
						break;
					}
				}

				if (id && id !== this.idLoaded) {
					navigationEvent = true;
				}
			}

			const actionWithResolve: ActionWithResolve = {
				...action,
				resolve,
			};

			if (navigationEvent) {
				this.lastNavigationSymbol = Symbol();
				this.clearQueue();
				this.initAction(actionWithResolve);
			} else if (this.queue.length === 0 && !this.processingAction) {
				this.initAction(actionWithResolve);
			} else {
				this.queue.push(actionWithResolve);
			}
		});
	}

	async initAction({
		url,
		refresh,
		replace,
		resolve,
		updateEvents,
		raw,
	}: ActionWithResolve) {
		this.processingAction = true;

		const state: any = {
			noTrack: refresh || replace,
			updateEvents,
			navigationSymbol: this.lastNavigationSymbol,
			...raw,
		};

		const actualURL = url ?? window.location.pathname + window.location.search;

		await router.navigate(actualURL, {
			state,
			refresh,

			// Would like to make this `replace: replace || url === undefined,` so it doesn't add a history entry on refreshes, but then Safari errors "Attempt to use history.replaceState() more than 100 times per 30 seconds"
			replace,
		});

		// router.navigate runs fromRouter, which waits until the content is displayed, so we can resolve the action here
		resolve();
	}

	initNextAction() {
		this.processingAction = false;

		const nextAction = this.queue.shift();
		if (nextAction) {
			this.initAction(nextAction);
		}
	}

	async processUpdate(
		{ Component, context, id, inLeague }: ViewInfo,
		navigationSymbol: symbol,
	) {
		actions.startLoading(id);
		const startedAt = Date.now();

		try {
			const updateEvents = context.state.updateEvents ?? [];

			let lidUrl: number | undefined;
			if (typeof context.params.lid === "string") {
				const newLidInt = Number.parseInt(context.params.lid);
				if (!Number.isNaN(newLidInt)) {
					lidUrl = newLidInt;
				}
			}

			let prevData;
			if (this.idLoaded !== id) {
				// This is the initial load of a page, so reset viewData and add firstRun update event
				if (!updateEvents.includes("firstRun")) {
					updateEvents.push("firstRun");
				}
				prevData = {};
			} else {
				prevData = {
					...this.viewData,
				};
			}

			const lidCurrent = local.getState().lid;

			// Previously this was only called if necessary (switching to a new league, or leaving a league) but sometimes Safari seems to kill/restart the worker and then league state (g, idb) needs to be reset. And that can happen at any time!
			await withPageLoadTimeout(
				toWorker("main", "beforeView", {
					inLeague,
					lidCurrent,
					lidUrl,
				}),
				startedAt,
			);

			if (!inLeague && lidCurrent !== undefined) {
				localActions.updateGameAttributes({
					lid: undefined,
				});
			}

			if (navigationSymbol !== this.lastNavigationSymbol) {
				this.initNextAction();
				return;
			}

			// ctxBBGM is hacky!
			const ctxBBGM = { ...context.state };
			delete ctxBBGM.err; // Can't send Error to worker
			delete ctxBBGM.navigationSymbol; // Can't send Symbol to worker

			// Resolve all the promises before updating the UI to minimize flicker
			const results = await withPageLoadTimeout(
				toWorker("main", "runBefore", {
					viewId: id,
					params: context.params,
					ctxBBGM,
					updateEvents,
					prevData,
				}),
				startedAt,
			);

			if (navigationSymbol !== this.lastNavigationSymbol) {
				this.initNextAction();
				return;
			}

			// If results is undefined, it means the league wasn't loaded yet at the time of the request, likely because another league was opening in another tab at the same time. So stop now and wait until we get a signal that there is a new league.
			if (results === undefined) {
				actions.doneLoading(id);
				this.initNextAction();
				return;
			}

			// If there was an error before, still show it unless we've received some other data. Otherwise, noop refreshes (return undefined from view, for non-matching updateEvent) would clear the error. Clear it only when some data is returned... which still is not great, because maybe the data is from a runBefore function that's different than the one that produced the error. Ideally would either need to track which runBefore function produced the error, this is a hack. THIS MAY NO LONGER BE TRUE AFTER CONSOLIDATING RUNBEFORE INTO A SINGLE FUNCTION, ideally the worker/views function could then handle conflicts itself. But currently the only ones returning errorMessage have just one function so it's either all or nothing.
			if (results && Object.keys(results).length > 0) {
				delete prevData.errorMessage;
			}

			let NewComponent = Component;

			if (
				prevData.errorMessage ||
				(results && Object.hasOwn(results, "errorMessage"))
			) {
				NewComponent = ErrorMessage;
			}

			const vars = {
				Component: NewComponent,
				data: Object.assign(prevData, results),
				loading: false,
				loadingError: undefined,
				idLoaded: id,
				idLoading: undefined,
				inLeague,
				scrollToTop:
					updateEvents.length === 1 && updateEvents[0] === "firstRun",
			};

			if (vars.data && vars.data.redirectUrl !== undefined) {
				// Wait a tick, otherwise there is a race condition on new page loads (such as reloading live_game box score) where initView is called and updates viewInfo while the local.subscribe subscription below is unsubscribed due to updatePage changing.
				await new Promise<void>((resolve) => {
					setTimeout(() => {
						resolve();
					}, 0);
				});

				realtimeUpdate(
					[],
					vars.data.redirectUrl,
					{
						backendRedirect: true,
					},
					true,
				);

				this.initNextAction();
				return;
			}

			actions.reset(vars);
			this.idLoaded = id;
			this.viewData = vars.data;

			this.initNextAction();
		} catch (error) {
			if (navigationSymbol === this.lastNavigationSymbol) {
				console.error("Error loading view:", error);
				actions.failLoading(id, error);
			}
			this.initNextAction();
		}
	}
}

export const viewManager = new ViewManager();
